// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: 26-versioning-update-strategy.md
import { useQueryClient } from '@tanstack/react-query';

import type { UpdaterRunSource } from '@/components/health/updaterRunSource';
import { api } from '@/lib/api';
import { thrownCode } from '@/lib/apiErrors';
import { HEALTH_POLL_MS, RUN_ACTIVE_POLL_MS } from '@/lib/healthPoll';
import { deriveListQuery } from '@/lib/listQuery';
import type { UpdateRunLogPage, UpdateRunSummary } from '@greenlight/shared';

/**
 * Chunks to ask for in one page when assembling the download. The capture is
 * bounded at 2 MB and stored in 256 KiB chunks, so this covers any transcript
 * the updater can produce and the limit is the endpoint's own ceiling.
 */
const MAX_TRANSCRIPT_CHUNKS = 100;

/** The install's own updater runs, read from its control plane. */
export const internalHealthRunSource: UpdaterRunSource<UpdateRunLogPage> = {
  useRuns({ limit, includeUpToDate, cursor }) {
    const queryClient = useQueryClient();
    const filter = includeUpToDate ? 'all' : 'changed';
    const listKey = ['updater-runs', filter, cursor ?? 'first'] as const;
    const query = api.health.updaterRuns.useQuery(
      listKey,
      {
        query: {
          limit,
          ...(includeUpToDate ? {} : { include_up_to_date: 'false' as const }),
          ...(cursor === undefined ? {} : { cursor }),
        },
      },
      {
        queryKey: listKey,
        // Read off the page it just fetched rather than a mirrored flag: an
        // interval that only turned up on the render after the run appeared would
        // spend that whole interval at the steady-state cadence. Only the newest
        // page can gain a run, so a page walked back to holds the slow cadence.
        refetchInterval: ({ state }) =>
          state.data?.status === 200 && state.data.body.items.some((run) => run.outcome === null)
            ? RUN_ACTIVE_POLL_MS
            : HEALTH_POLL_MS,
      },
    );
    const { items, nextCursor, hasError } = deriveListQuery<UpdateRunSummary>(query);
    return {
      items,
      nextCursor,
      hasError,
      isLoading: query.isLoading,
      isFetching: query.isFetching,
      refetch: () => void query.refetch(),
      // The app-wide 30s staleTime would otherwise serve the head from cache.
      invalidateHead: () =>
        void queryClient.invalidateQueries({ queryKey: ['updater-runs', filter, 'first'] }),
    };
  },

  useRun(runId) {
    const detailKey = ['updater-run', runId ?? ''];
    const query = api.health.updaterRun.useQuery(
      detailKey,
      { params: { id: runId ?? '' } },
      {
        queryKey: detailKey,
        enabled: runId !== null,
        // An open run records a phase every few minutes, so the drawer follows it
        // at the run cadence; a finished run never changes again.
        refetchInterval: ({ state }) =>
          state.data?.status === 200 && state.data.body.outcome === null
            ? RUN_ACTIVE_POLL_MS
            : HEALTH_POLL_MS,
      },
    );
    return {
      run: query.data?.status === 200 ? query.data.body : null,
      isLoading: query.isLoading,
      isError: query.isError,
      isFetching: query.isFetching,
      // Retention prunes run rows, so a link an operator kept can outlive its run.
      notFound: thrownCode(query.error) === 'updater.run_not_found',
      refetch: () => void query.refetch(),
    };
  },

  log(run) {
    return {
      usePage(cursor) {
        const queryKey = ['updater-run-log', run.id, cursor ?? 'first'];
        const query = api.health.updaterRunLog.useQuery(
          queryKey,
          { params: { id: run.id }, query: cursor === undefined ? {} : { cursor } },
          { queryKey, enabled: (run.transcript?.chunk_count ?? 0) > 0 },
        );
        return {
          page: query.data?.status === 200 ? query.data.body : null,
          isLoading: query.isLoading,
          isError: query.isError,
          isFetching: query.isFetching,
          refetch: () => void query.refetch(),
        };
      },
      async readAll() {
        const whole = await api.health.updaterRunLog.query({
          params: { id: run.id },
          query: { limit: MAX_TRANSCRIPT_CHUNKS },
        });
        if (whole.status !== 200) throw new Error(`transcript read returned ${whole.status}`);
        return whole.body.text;
      },
    };
  },
};
